import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Button } from '@/shared/ui/buttons/Button';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiParcelsRejectPost } from '@/entities/parcels';
import type { ApiParcelsGetItem } from '@/shared/types/api/parcels';

type Props = {
  parcel: ApiParcelsGetItem | null;
  onClose: () => void;
};

export const FeaturesParcelsRejectDialog = ({ parcel, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [reason, setReason] = useState('');

  const reject = useMutate({
    api: ({ id, r }: { id: string; r: string }) =>
      apiParcelsRejectPost(id, r ? { reason: r } : {}),
    invalidateKeys: [queryKeys.parcels.all],
    onSuccess: () => {
      toast.info(t('parcels.toast.rejected'));
      setReason('');
      onClose();
    },
    onError: (_e, message) => toast.error(message),
  });

  if (!parcel) return null;

  return (
    <Dialog
      open={Boolean(parcel)}
      onClose={() => {
        setReason('');
        onClose();
      }}
      title={t('parcels.reject.title')}
      description={t('parcels.reject.description', { name: parcel.name })}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              setReason('');
              onClose();
            }}
            disabled={reject.isPending}
          >
            {t('parcels.reject.cancel')}
          </Button>
          <Button
            variant="danger"
            loading={reject.isPending}
            onClick={() => reject.mutate({ id: parcel.id, r: reason.trim() })}
          >
            {t('parcels.reject.confirm')}
          </Button>
        </>
      }
    >
      <Textarea
        label={t('parcels.reject.reasonLabel')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder={t('parcels.reject.reasonPlaceholder')}
      />
    </Dialog>
  );
};
